import { c as _c } from "react/compiler-runtime";
import { Check, SquareArrowOutUpRight, MoreHorizontal } from "lucide-react";
import Link from "next/link";
import React from "react";
import { CardActions } from "@/src/components/cards/card-actions";
import { ComparisonField } from "@/src/components/cards/comparison-field";
import { CopyableContent } from "@/src/components/copyable-content";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { Button } from "@/src/components/shadcn/ui/button";
import { DropdownMenu, DropdownMenuTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import { cn } from "@/src/lib/shadcn-utils";
import { getNestedValue } from "@/src/lib/utils/object-utils";
import { compareStrings, getDisplayValue, normalizeForComparison, FieldType } from "@/src/lib/utils/string-utils";
export interface Field {
  key: string;
  label: string;
  type?: FieldType;
  comparable?: boolean;
  required?: boolean;
  colSpan?: 1 | 2 | 3;
  wrap?: boolean;
  hideIfEmpty?: boolean;
  sectionHeader?: string;
  // Cards supply callbacks for different model types through this shared field descriptor.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue?: (obj: any) => string | null | undefined;
}
function resolveFieldValue(field: Field, obj: unknown): string | null | undefined {
  if (field.getValue) {
    return field.getValue(obj);
  }
  return getNestedValue(obj, field.key);
}
export interface FieldDisplayProps {
  field: Field;
  primaryValue: string | null;
  secondaryValue?: string | null;
  isCompareMode: boolean;
  compareAgainstTitle?: string;
  stacked?: boolean;
}
export function FieldDisplay(t0) {
  const $ = _c(24);
  const {
    field,
    primaryValue,
    secondaryValue,
    isCompareMode,
    compareAgainstTitle: t1,
    stacked: t2
  } = t0;
  const compareAgainstTitle = t1 === undefined ? "EMR" : t1;
  const stacked = t2 === undefined ? false : t2;
  if (isCompareMode && secondaryValue !== null && field.comparable) {
    const t3 = primaryValue || "N/A";
    const t4 = secondaryValue || "N/A";
    const t5 = field.type ?? "text";
    let t6;
    if ($[0] !== compareAgainstTitle || $[1] !== field.label || $[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
      t6 = <ComparisonField label={field.label} value={t3} secondaryValue={t4} type={t5} compareAgainstTitle={compareAgainstTitle} />;
      $[0] = compareAgainstTitle;
      $[1] = field.label;
      $[2] = t3;
      $[3] = t4;
      $[4] = t5;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    return t6;
  }
  const t3 = field.type ?? "text";
  let t4;
  if ($[6] !== primaryValue || $[7] !== t3) {
    t4 = getDisplayValue(primaryValue, t3);
    $[6] = primaryValue;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const displayValue = t4;
  if (stacked) {
    let t5;
    if ($[9] !== field.label) {
      t5 = <div className="text-xs font-medium text-muted-foreground">{field.label}</div>;
      $[9] = field.label;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== displayValue) {
      t6 = <div className="leading-5"><CopyableContent text={displayValue} /></div>;
      $[11] = displayValue;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t5 || $[14] !== t6) {
      t7 = <div className="min-w-0 space-y-0.5 whitespace-normal [overflow-wrap:anywhere]">{t5}{t6}</div>;
      $[13] = t5;
      $[14] = t6;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    return t7;
  }
  const t5 = field.wrap ? "break-words" : "overflow-hidden text-ellipsis whitespace-nowrap";
  let t6;
  if ($[16] !== field.label) {
    t6 = <span className="font-medium">{field.label}:</span>;
    $[16] = field.label;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== displayValue) {
    t7 = <CopyableContent text={displayValue} />;
    $[18] = displayValue;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className={t5}>{t6} {t7}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
export function FieldLayout(t0) {
  const $ = _c(20);
  const {
    fields,
    patient,
    secondaryObject,
    compareMode: t1,
    compareAgainstTitle: t2,
    stacked: t3
  } = t0;
  const compareMode = t1 === undefined ? false : t1;
  const compareAgainstTitle = t2 === undefined ? "EMR" : t2;
  const stacked = t3 === undefined ? false : t3;
  const t4 = stacked ? "grid-cols-[repeat(auto-fit,minmax(min(100%,16rem),1fr))] gap-y-2" : "grid-cols-3 gap-y-2";
  let t5;
  if ($[0] !== t4) {
    t5 = cn("grid gap-x-4 text-sm", t4);
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== compareAgainstTitle || $[3] !== compareMode || $[4] !== fields || $[5] !== patient || $[6] !== secondaryObject || $[7] !== stacked) {
    let t7;
    if ($[9] !== patient) {
      t7 = field => !field.hideIfEmpty || resolveFieldValue(field, patient);
      $[9] = patient;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== compareAgainstTitle || $[12] !== compareMode || $[13] !== patient || $[14] !== secondaryObject || $[15] !== stacked) {
      t8 = field_0 => <React.Fragment key={field_0.key}>{field_0.sectionHeader && <div className="col-span-full mt-1 border-t border-border pt-2"><span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{field_0.sectionHeader}</span></div>}<div className={cn(stacked && "min-w-0", stacked && field_0.colSpan && field_0.colSpan > 1 && "col-span-full", !stacked && field_0.colSpan === 3 && "col-span-3", !stacked && field_0.colSpan === 2 && "col-span-2", field_0.wrap && "min-w-0")}><FieldDisplay field={field_0} primaryValue={resolveFieldValue(field_0, patient) || "N/A"} secondaryValue={secondaryObject ? resolveFieldValue(field_0, secondaryObject) || "N/A" : null} isCompareMode={compareMode} compareAgainstTitle={compareAgainstTitle} stacked={stacked} /></div></React.Fragment>;
      $[11] = compareAgainstTitle;
      $[12] = compareMode;
      $[13] = patient;
      $[14] = secondaryObject;
      $[15] = stacked;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t6 = fields.filter(t7).map(t8);
    $[2] = compareAgainstTitle;
    $[3] = compareMode;
    $[4] = fields;
    $[5] = patient;
    $[6] = secondaryObject;
    $[7] = stacked;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
export function hasConflicts(fields: Field[], obj: unknown, secondaryObj: unknown) {
  const comparableFields = fields.filter(field => field.comparable);
  return comparableFields.some(field => {
    const fieldType = field.type ?? "text";
    const primaryValue = normalizeForComparison(getNestedValue(obj, field.key) || "", fieldType);
    const secondaryValue = normalizeForComparison(getNestedValue(secondaryObj, field.key) || "", fieldType);
    return !compareStrings(primaryValue, secondaryValue, fieldType);
  });
}
export function renderBadge(compareMode: boolean, secondaryObject: unknown, hasAnyConflicts: boolean) {
  if (!compareMode) {
    return null;
  }
  if (!secondaryObject) {
    return <MissingDataBadge />;
  }
  if (hasAnyConflicts) {
    return <ConflictsBadge />;
  }
  return <SyncedBadge />;
}
export function SyncedBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="text-success border-success/30 bg-success/10"><Check className="h-3 w-3 mr-1" />Synced</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function ConflictsBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="text-warn border-warn/30 bg-warn/10">Conflicts</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function EhrDataBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="bg-ehr/10 text-ehr">EHR Data</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function MissingDataBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="text-destructive border-destructive/30 bg-destructive/10">Missing EHR Data</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function LinkToEntity(t0) {
  const $ = _c(3);
  const {
    href
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SquareArrowOutUpRight className="size-4" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== href) {
    t2 = <Link className="text-muted-foreground hover:text-foreground" href={href} target="_blank" rel="noopener noreferrer">{t1}</Link>;
    $[1] = href;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function EntityDropdownMenu(t0) {
  const $ = _c(11);
  const {
    cardActionsProps,
    customProps: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const customProps = t2;
  let t3;
  if ($[2] !== cardActionsProps.customProps) {
    t3 = cardActionsProps.customProps ?? [];
    $[2] = cardActionsProps.customProps;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== customProps || $[5] !== t3) {
    t4 = [...t3, ...customProps];
    $[4] = customProps;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const mergedCustomProps = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"><MoreHorizontal className="h-4 w-4" /><span className="sr-only">Open menu</span></Button></DropdownMenuTrigger>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== cardActionsProps || $[9] !== mergedCustomProps) {
    t6 = <DropdownMenu>{t5}<CardActions {...cardActionsProps} isContextMenu={false} customProps={mergedCustomProps} /></DropdownMenu>;
    $[8] = cardActionsProps;
    $[9] = mergedCustomProps;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}